import React, { useMemo, useState } from 'react';
import { BONUS_LIST } from '../BonusList';
import { BONUS_PRESETS } from '../BonusPresets';
import {
  getBonusPointsForShow,
  getOrderedBonusesForShow,
  getShowBonusCategory,
} from '../../utils/bonusConfig';
import { formatSuggestedShowLabel } from '../../utils/titleBonusSuggestions';
import '../../styles/ShowEditor.css';

const DUAL_POINT_BONUS_VALUES = new Set([
  'opp_titolata',
  'opp_titolata_mondiale',
  'mantiene_titolo',
  'vince_titolo_tag',
  'vince_titolo_singolo',
  'vince_titolo_massimo',
  'doppio_campione',
  'campione_100',
  'campione_200',
  'campione_280',
  'campione_365',
]);

const getBonusListEntries = (bonuses) => (
  bonuses.flatMap((bonus) => {
    if (
      !DUAL_POINT_BONUS_VALUES.has(bonus.value) ||
      typeof bonus.points !== 'number' ||
      typeof bonus.pointsExt !== 'number'
    ) {
      return [bonus];
    }

    return [
      {
        ...bonus,
        listValue: `${bonus.value}__points`,
        listVariant: 'MAIN',
        listPoints: bonus.points,
      },
      {
        ...bonus,
        listValue: `${bonus.value}__pointsExt`,
        listVariant: 'extra',
        listPoints: bonus.pointsExt,
      },
    ];
  })
);

const getBonusListPoints = (bonus, showData) => (
  typeof bonus.listPoints === 'number'
    ? bonus.listPoints
    : getBonusPointsForShow(bonus, showData)
);

const isSelectedBonusRow = (selectedBonus, bonus, bonusPoints) => {
  if (!selectedBonus || selectedBonus.value !== bonus.value) return false;
  if (!bonus.listVariant) return true;

  return selectedBonus.points === bonusPoints;
};

const WrestlerSelectionSection = React.memo(({
  selectedWrestlers,
  onSelect,
  onRemove,
  availableWrestlers,
  allWrestlers,
  savedTeams = [],
  searchQuery,
  setSearchQuery,
  disabledWrestlers = [],
  liveParticipations = [],
  draftParticipations = [],
  showName = '',
  participationMode = 'live',
  bonusSuggestionsByWrestler = {}
}) => {
  const selectedWrestlerSet = useMemo(() => new Set(selectedWrestlers), [selectedWrestlers]);

  const filteredWrestlers = useMemo(() => {
    if (!searchQuery) return availableWrestlers;
    const query = searchQuery.toLowerCase();
    return availableWrestlers.filter((wrestler) => wrestler.name.toLowerCase().includes(query));
  }, [availableWrestlers, searchQuery]);

  const sortedWrestlers = useMemo(() => {
    const liveIds = new Set(liveParticipations.map((participation) => participation.wrestlerId));
    const draftIds = new Set(draftParticipations.map((participation) => participation.wrestlerId));
    const showNameLower = (showName || '').toLowerCase();
    const rawSmackdown = ['raw', 'smackdown'];

    const wrestlers = filteredWrestlers.map((wrestler) => ({
      ...wrestler,
    }));

    return [...wrestlers].sort((a, b) => {
      const aLive = liveIds.has(a.id);
      const bLive = liveIds.has(b.id);
      if (aLive !== bLive) return aLive ? -1 : 1;

      const aDraft = draftIds.has(a.id);
      const bDraft = draftIds.has(b.id);
      if (aDraft !== bDraft) return aDraft ? -1 : 1;


      return a.name.localeCompare(b.name);
    });
  }, [draftParticipations, filteredWrestlers, liveParticipations, showName]);

  const teamSuggestions = useMemo(() => {
    const availableIds = new Set(availableWrestlers.map((wrestler) => wrestler.id));
    const selectedIds = new Set(selectedWrestlers);
    const disabledIds = new Set(disabledWrestlers);

    if (selectedIds.size === 0) return [];

    return savedTeams
      .map((team) => {
        const members = (team.members || []).filter((memberId) => availableIds.has(memberId) && !disabledIds.has(memberId));
        const selectedMembers = members.filter((memberId) => selectedIds.has(memberId));
        return {
          ...team,
          members,
          selectableMembers: members.filter((memberId) => !selectedIds.has(memberId)),
          selectedMembers
        };
      })
      .filter((team) => team.members.length > 0 && team.selectedMembers.length > 0);
  }, [availableWrestlers, disabledWrestlers, savedTeams, selectedWrestlers]);

  const handleTeamSelect = (team) => {
    team.selectableMembers.forEach((memberId) => onSelect(memberId));
  };

  const handleSelectableRowKeyDown = (event, wrestlerId, isDisabled) => {
    if (isDisabled || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    onSelect(wrestlerId);
  };

  return (
    <div className="mb-4 show-editor-section">
      <label className="form-label fw-semibold">
        <i className="bi bi-person-plus me-1"></i>
        Seleziona wrestler
      </label>

      <div className="input-group mb-3">
        <span className="input-group-text">
          <i className="bi bi-search"></i>
        </span>
        <input
          type="text"
          className="form-control"
          placeholder="Cerca wrestler..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="border rounded show-editor-scroll-list show-editor-scroll-list--medium" style={{ maxHeight: '300px', overflowY: 'auto' }}>
        {sortedWrestlers.length === 0 ? (
          <div className="text-center py-3 text-muted small show-editor-empty">
            {searchQuery ? 'Nessun wrestler trovato' : 'Nessun wrestler disponibile'}
          </div>
        ) : (
          <div className="list-group list-group-flush">
            {sortedWrestlers.map((wrestler) => {
              const isAlreadyLive = liveParticipations.some((participation) => participation.wrestlerId === wrestler.id);
              const isAlreadyDraft = draftParticipations.some((participation) => participation.wrestlerId === wrestler.id);
              const isDisabled = disabledWrestlers.includes(wrestler.id);
              const isSelected = selectedWrestlerSet.has(wrestler.id);
              const hasTitleSuggestion = Array.isArray(bonusSuggestionsByWrestler[wrestler.id]) && bonusSuggestionsByWrestler[wrestler.id].length > 0;
              const rowMeta = isAlreadyLive
                ? 'Gia in live'
                : isAlreadyDraft && participationMode === 'live'
                ? 'Gia in draft, puo andare anche in live'
                :  '';

              return (
                <div
                  key={wrestler.id}
                  className={`list-group-item list-group-item-action py-2 px-3 show-editor-list-item show-editor-pick-row ${isSelected ? 'is-selected' : ''} ${isDisabled ? 'is-disabled opacity-50' : ''}`}
                  onClick={() => !isDisabled && onSelect(wrestler.id)}
                  onKeyDown={(event) => handleSelectableRowKeyDown(event, wrestler.id, isDisabled)}
                  role="button"
                  tabIndex={isDisabled ? -1 : 0}
                  style={{
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    fontSize: '0.875rem'
                  }}
                >
                  <div className="d-flex align-items-center">
                    <input
                      type="checkbox"
                      className="form-check-input me-3"
                      checked={isSelected}
                      onChange={() => !isDisabled && onSelect(wrestler.id)}
                      disabled={isDisabled}
                      onClick={(e) => e.stopPropagation()}
                      id={`wrestler-${wrestler.id}`}
                    />
                    <label
                      className="flex-grow-1 mb-0"
                      htmlFor={`wrestler-${wrestler.id}`}
                      onClick={(e) => e.stopPropagation()}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="show-editor-pick-row__title">
                        {wrestler.name}
                        {hasTitleSuggestion ? (
                          <span
                            className="show-editor-alert-dot"
                            title="Questo wrestler ha un bonus o malus titolo suggerito"
                            aria-label="Bonus titolo suggerito"
                          ></span>
                        ) : null}
                      </div>
                      {rowMeta ? <small className="show-editor-pick-row__meta">{rowMeta}</small> : null}
                    </label>
                    {isSelected ? <i className="bi bi-check2 show-editor-pick-row__icon"></i> : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {teamSuggestions.length > 0 && (
        <div className="border rounded p-2 mt-3 bg-light show-editor-panel show-editor-panel--compact">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <small className="fw-semibold text-muted">
              <i className="bi bi-people-fill me-1"></i>
              Team rapidi
            </small>
          </div>
          <div className="d-flex flex-wrap gap-2">
            {teamSuggestions.map((team) => (
              <button
                key={team.id}
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={() => handleTeamSelect(team)}
                disabled={team.selectableMembers.length === 0}
                title={team.memberNames?.join(', ') || team.name}
              >
                <i className="bi bi-plus-circle me-1"></i>
                {team.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {selectedWrestlers.length > 0 && (
        <div className="mb-3 p-3 border rounded bg-light mt-3 show-editor-panel">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <small className="text-muted">
              <i className="bi bi-check2-circle me-1"></i>
              {selectedWrestlers.length} selezionati
            </small>
            <button
              type="button"
              className="btn btn-link btn-sm text-decoration-none p-0"
              onClick={() => {
                selectedWrestlers.forEach((id) => onRemove(id));
              }}
            >
              <small className="text-danger">
                <i className="bi bi-x-circle me-1"></i>
                Rimuovi tutti
              </small>
            </button>
          </div>
          <div className="show-editor-chip-row">
            {selectedWrestlers.map((wrestlerId) => {
              const wrestler = allWrestlers.find((item) => item.id === wrestlerId);
              return (
                <span key={wrestlerId} className="show-editor-chip">
                  {wrestler?.name || 'Sconosciuto'}
                  <button
                    type="button"
                    className="btn-close ms-1"
                    onClick={() => onRemove(wrestlerId)}
                    aria-label="Rimuovi"
                    style={{ fontSize: '0.6rem' }}
                  ></button>
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
});

function ParticipationForm({
  showData,
  wrestlers,
  participations,
  liveParticipations = [],
  draftParticipations = [],
  selectedWrestlers,
  onWrestlerSelect,
  onWrestlerRemove,
  selectedBonuses,
  onBonusToggle,
  onBonusQtyChange,
  totalPoints,
  onSubmit,
  onCancel,
  editingParticipationId,
  isBulkEdit,
  selectedParticipationsForEdit,
  searchQuery,
  onSearchQueryChange,
  showPresets,
  onTogglePresets,
  selectedPreset,
  onApplyPreset,
  savedTeams = [],
  participationMode = 'live',
  onParticipationModeChange,
  showName = '',
  isLiveBonusLocked = false,
  liveBonusUnlockLabel = '',
  bonusSuggestionsByWrestler = {},
  bonusSuggestionsLoading = false
}) {
  const [bonusSearchQuery, setBonusSearchQuery] = useState('');
  const canEditBonuses = participationMode === 'draft' || !isLiveBonusLocked;
  const isLiveAnnouncementMode = participationMode === 'live' && isLiveBonusLocked;
  const isBonusBulkEditDisabled = isBulkEdit && isLiveAnnouncementMode;

  const filteredBonuses = useMemo(() => {
    const orderedBonuses = getBonusListEntries(getOrderedBonusesForShow(BONUS_LIST, showData));
    const query = bonusSearchQuery.trim().toLowerCase();

    if (!query) return orderedBonuses;

    return orderedBonuses.filter((bonus) => (
      bonus.label?.toLowerCase().includes(query) ||
      bonus.listVariant?.toLowerCase().includes(query) ||
      bonus.desc?.toLowerCase().includes(query) ||
      bonus.value?.toLowerCase().includes(query)
    ));
  }, [bonusSearchQuery, showData]);

  const availableWrestlers = useMemo(() => {
    const blockedIds = new Set();
    if (participationMode === 'live') {
      liveParticipations.forEach((participation) => blockedIds.add(participation.wrestlerId));
    } else {
      draftParticipations.forEach((participation) => blockedIds.add(participation.wrestlerId));
    }

    let filtered = wrestlers.filter((wrestler) => wrestler.active !== false && !blockedIds.has(wrestler.id));

    if (editingParticipationId) {
      const currentPart = participations.find((participation) => participation.id === editingParticipationId);
      if (currentPart && !filtered.some((wrestler) => wrestler.id === currentPart.wrestlerId)) {
        const currentWrestler = wrestlers.find((wrestler) => wrestler.id === currentPart.wrestlerId);
        if (currentWrestler) filtered = [...filtered, currentWrestler];
      }
    }

    const liveIds = new Set(liveParticipations.map((participation) => participation.wrestlerId));
    const showNameLower = (showName || '').toLowerCase();
    const rawSmackdown = ['raw', 'smackdown'];

    return [...filtered].sort((a, b) => {
      const aLive = liveIds.has(a.id);
      const bLive = liveIds.has(b.id);
      if (aLive !== bLive) return aLive ? -1 : 1;


      return a.name.localeCompare(b.name);
    });
  }, [wrestlers, participationMode, liveParticipations, draftParticipations, editingParticipationId, participations, showName]);

  const handleSubmit = (event) => {
    event?.preventDefault?.();
    if (typeof onSubmit === 'function') onSubmit();
  };

  const handleBonusRowToggle = (bonus, bonusPoints) => {
    const selectedBonus = selectedBonuses.find((item) => item.value === bonus.value);
    const isSameRowSelected = isSelectedBonusRow(selectedBonus, bonus, bonusPoints);
    const normalizedBonus = {
      value: bonus.value,
      label: bonus.label,
      points: bonusPoints
    };

    if (selectedBonus && !isSameRowSelected) {
      onBonusToggle({ value: bonus.value });
      onBonusToggle(normalizedBonus);
      return;
    }

    onBonusToggle(normalizedBonus);
  };

  const handleBonusRowKeyDown = (event, bonus, bonusPoints) => {
    if (event.target !== event.currentTarget) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    handleBonusRowToggle(bonus, bonusPoints);
  };

  const disabledWrestlersIds = participationMode === 'draft'
    ? draftParticipations.map((participation) => participation.wrestlerId)
    : liveParticipations.map((participation) => participation.wrestlerId);

  const primaryActionLabel = isBulkEdit
    ? 'Aggiungi bonus'
    : editingParticipationId
    ? participationMode === 'draft'
      ? 'Aggiorna bozza'
      : isLiveAnnouncementMode
      ? 'Conferma live'
      : 'Aggiorna live'
    : participationMode === 'draft'
    ? 'Salva in draft'
    : isLiveAnnouncementMode
    ? 'Annuncia in live'
    : 'Salva in live';
  const focusWrestlerId = useMemo(() => {
    if (isBulkEdit) return '';

    if (editingParticipationId) {
      const editedParticipation = participations.find(
        (participation) => participation.id === editingParticipationId
      );
      return editedParticipation?.wrestlerId || selectedWrestlers[0] || '';
    }

    return selectedWrestlers.length === 1 ? selectedWrestlers[0] : '';
  }, [editingParticipationId, isBulkEdit, participations, selectedWrestlers]);
  const focusBonusSuggestions = useMemo(() => {
    if (!focusWrestlerId) return [];
    return Array.isArray(bonusSuggestionsByWrestler[focusWrestlerId])
      ? bonusSuggestionsByWrestler[focusWrestlerId]
      : [];
  }, [bonusSuggestionsByWrestler, focusWrestlerId]);

  return (
    <div className="card shadow-sm border-0 show-editor-card show-participation-form">
      <div className={`card-header ${participationMode === 'draft' ? 'bg-warning text-dark show-editor-card__header--warning' : 'bg-primary text-white show-editor-card__header--primary'} py-3 show-editor-card__header`}>
        <h3 className={`h5 mb-0 ${participationMode === 'draft' ? 'text-dark' : 'text-white'}`}>
          <i className={`bi ${participationMode === 'draft' ? 'bi-pencil-square' : 'bi-plus-circle'} me-2`}></i>
          {isBulkEdit ? 'Modifica Multipla' : editingParticipationId ? `Modifica ${participationMode === 'draft' ? 'Bozza' : 'Partecipazione'}` : `Nuova ${participationMode === 'draft' ? 'Bozza' : 'Partecipazione'}`}
        </h3>
      </div>
      <div className="card-body show-editor-card__body">
        <form onSubmit={handleSubmit}>
          <div className="mb-4 show-editor-section">
            <label className="form-label fw-semibold">
              <i className="bi bi-toggles me-1"></i>
              Modalita
            </label>
            <div className="show-editor-mode-grid">
              <button
                type="button"
                className={`btn btn-sm show-editor-mode-button ${participationMode === 'live' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => onParticipationModeChange && onParticipationModeChange('live')}
              >
                <span className="show-editor-mode-button__title">Live {liveParticipations.length}</span>
              </button>
              <button
                type="button"
                className={`btn btn-sm show-editor-mode-button ${participationMode === 'draft' ? 'btn-warning text-dark' : 'btn-outline-warning'}`}
                onClick={() => onParticipationModeChange && onParticipationModeChange('draft')}
              >
                <span className="show-editor-mode-button__title">Draft {draftParticipations.length}</span>
              </button>
            </div>
          </div>

          {isLiveAnnouncementMode && (
            <div className="show-editor-panel show-editor-panel--info">
              <strong>Live rapido attivo</strong>
              <small>
                Fino a {liveBonusUnlockLabel || '48 ore prima dello show'} puoi annunciare i wrestler in live senza bonus.
                Per bonus e punteggi continua a lavorare nel draft.
              </small>
            </div>
          )}

          {isBulkEdit ? (
            <div className="alert alert-info mb-4 py-2">
              <i className="bi bi-info-circle me-2"></i>
              <strong>Modalita modifica multipla:</strong><br />
              Stai per aggiungere bonus a <strong>{selectedParticipationsForEdit.length} {participationMode === 'draft' ? 'bozze' : 'partecipazioni'}</strong>.
            </div>
          ) : editingParticipationId ? (
            <div className="mb-4 show-editor-section">
              <label className="form-label fw-semibold">
                <i className="bi bi-person me-1"></i>
                Wrestler
              </label>
              <div className="border rounded p-2 bg-light show-editor-panel">
                <strong>
                  {participations.find((participation) => participation.id === editingParticipationId)?.wrestlerName ||
                    wrestlers.find((wrestler) => wrestler.id === selectedWrestlers[0])?.name ||
                    'Sconosciuto'}
                </strong>
              </div>
            </div>
          ) : (
            <WrestlerSelectionSection
              key={`${participationMode}-${showName}`}
              selectedWrestlers={selectedWrestlers}
              onSelect={onWrestlerSelect}
              onRemove={onWrestlerRemove}
              availableWrestlers={availableWrestlers}
              allWrestlers={wrestlers}
              savedTeams={savedTeams}
              searchQuery={searchQuery}
              setSearchQuery={onSearchQueryChange}
              disabledWrestlers={disabledWrestlersIds}
              liveParticipations={liveParticipations}
              draftParticipations={draftParticipations}
              showName={showName}
              participationMode={participationMode}
              bonusSuggestionsByWrestler={bonusSuggestionsByWrestler}
            />
          )}

          {canEditBonuses ? (
            <>
              {focusWrestlerId && (bonusSuggestionsLoading || focusBonusSuggestions.length > 0) && (
                <div className="show-editor-panel show-editor-panel--suggestions">
                  <div className="show-editor-suggestion-header">
                    <strong>
                      <i className="bi bi-trophy me-2"></i>
                      Suggerimenti bonus titolo
                    </strong>
                    <small>
                      Controllo basato sui giorni da campione e dall&apos;ultima difesa.
                    </small>
                  </div>

                  {bonusSuggestionsLoading ? (
                    <div className="show-editor-suggestion-card">
                      <small>Sto controllando i bonus titolati suggeriti per questo wrestler...</small>
                    </div>
                  ) : (
                    <div className="show-editor-suggestion-list">
                      {focusBonusSuggestions.map((suggestion) => (
                        <div
                          key={suggestion.id}
                          className={`show-editor-suggestion-card ${suggestion.isCurrentShowSuggestion ? 'is-current' : ''}`}
                        >
                          <div className="show-editor-suggestion-card__top">
                            <strong>{suggestion.bonusLabel}</strong>
                            <span className={`show-editor-suggestion-pill ${suggestion.isCurrentShowSuggestion ? 'is-current' : ''}`}>
                              {suggestion.isCurrentShowSuggestion ? 'Questo show' : 'Altro show'}
                            </span>
                          </div>
                          <small>{suggestion.titleName}</small>
                          <small>
                            {suggestion.source === 'reign'
                              ? `${suggestion.currentCount} giorni da campione`
                              : `${suggestion.currentCount} giorni dall'ultima difesa titolata`}
                          </small>
                          <small>
                            Show suggerito:{' '}
                            {suggestion.isCurrentShowSuggestion
                              ? `questo show (${formatSuggestedShowLabel(suggestion.suggestedShow)})`
                              : formatSuggestedShowLabel(suggestion.suggestedShow)}
                          </small>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="mb-4 show-editor-section">
                <label className="form-label fw-semibold">
                  <i className="bi bi-star me-1"></i>
                  Bonus e malus
                </label>

                <div className="input-group mb-3">
                  <span className="input-group-text">
                    <i className="bi bi-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Cerca bonus o malus..."
                    value={bonusSearchQuery}
                    onChange={(e) => setBonusSearchQuery(e.target.value)}
                  />
                  {bonusSearchQuery && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setBonusSearchQuery('')}
                      aria-label="Cancella ricerca bonus"
                    >
                      <i className="bi bi-x-lg"></i>
                    </button>
                  )}
                </div>

                <div className="border rounded show-editor-scroll-list show-editor-scroll-list--medium" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                  {filteredBonuses.length === 0 ? (
                    <div className="text-center py-3 text-muted small show-editor-empty">
                      Nessun bonus trovato
                    </div>
                  ) : filteredBonuses.map((bonus) => {
                    const bonusPoints = getBonusListPoints(bonus, showData);
                    const selectedBonus = selectedBonuses.find((item) => isSelectedBonusRow(item, bonus, bonusPoints));
                    const listValue = bonus.listValue || bonus.value;
                    return (
                      <div
                        key={listValue}
                        className={`d-flex align-items-center py-2 px-3 border-bottom show-bonus-row ${selectedBonus ? 'is-selected' : ''}`}
                        onClick={() => handleBonusRowToggle(bonus, bonusPoints)}
                        onKeyDown={(event) => handleBonusRowKeyDown(event, bonus, bonusPoints)}
                        role="button"
                        tabIndex={0}
                      >
                        <div className="form-check flex-grow-1">
                          <input
                            type="checkbox"
                            id={`bonus-${listValue}`}
                            className="form-check-input"
                            checked={!!selectedBonus}
                            onChange={() => handleBonusRowToggle(bonus, bonusPoints)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <label
                            htmlFor={`bonus-${listValue}`}
                            className="form-check-label ms-2 small d-flex align-items-center justify-content-between gap-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span>
                              {bonus.label}
                              {bonus.listVariant && (
                                <span className="ms-2 text-muted">({bonus.listVariant})</span>
                              )}
                            </span>
                            <strong className={`show-bonus-row__points ${bonusPoints >= 0 ? 'is-positive' : 'is-negative'}`}>
                              {bonusPoints >= 0 ? '+' : ''}{bonusPoints}
                            </strong>
                          </label>
                        </div>
                        {selectedBonus && (
                          <div className="d-flex align-items-center ms-2">
                            <input
                              type="number"
                              min="1"
                              max="99"
                              className="form-control form-control-sm"
                              style={{ width: '60px' }}
                              value={selectedBonus.qty}
                              onChange={(e) => onBonusQtyChange(bonus.value, parseInt(e.target.value, 10) || 1)}
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                            />
                            <span className="ms-1 small text-muted">x</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="mb-4 show-editor-section">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <label className="form-label fw-semibold mb-0">
                    <i className="bi bi-lightning me-1"></i>
                    Preset
                  </label>
                  <button
                    type="button"
                    className="btn btn-sm btn-light"
                    onClick={() => onTogglePresets && onTogglePresets()}
                    aria-label={showPresets ? 'Nascondi preset' : 'Mostra preset'}
                  >
                    <i className={`bi ${showPresets ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                  </button>
                </div>
                {showPresets && (
                  <div className="d-flex flex-wrap gap-2">
                    {BONUS_PRESETS
                      .filter((preset) => {
                        const showCategory = getShowBonusCategory(showData);
                        return preset.cat === showCategory || preset.cat === 'both';
                      })
                      .map((preset) => (
                        <button
                          type="button"
                          key={preset.label}
                          className={`btn btn-sm ${selectedPreset === preset.label ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => onApplyPreset && onApplyPreset(preset)}
                        >
                          {preset.label}
                        </button>
                      ))}
                  </div>
                )}
              </div>

              <div className={`alert ${totalPoints >= 0 ? 'alert-success' : 'alert-danger'} py-2 mb-4 show-editor-total`}>
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fw-semibold">
                    <i className="bi bi-calculator me-1"></i>
                    Totale punti
                  </span>
                  <span className="fw-bold fs-5">
                    {totalPoints >= 0 ? '+' : ''}{totalPoints}
                  </span>
                </div>
              </div>

              {participationMode === 'draft' && !editingParticipationId && !isBulkEdit && (
                <div className="alert alert-warning py-2 mb-4">
                  <i className="bi bi-pencil-square me-2"></i>
                  La partecipazione verra salvata in <strong>draft</strong>.
                </div>
              )}
            </>
          ) : null}

          <div className="d-flex gap-2 pt-3 border-top show-editor-actions">
            {(editingParticipationId || isBulkEdit) && (
              <button
                type="button"
                className="btn btn-outline-secondary flex-grow-1"
                onClick={() => onCancel && onCancel()}
              >
                <i className="bi bi-x-circle me-1"></i>
                Annulla
              </button>
            )}
            <button
              type="submit"
              className={`btn ${isBulkEdit || editingParticipationId ? 'btn-warning' : participationMode === 'draft' ? 'btn-outline-warning' : 'btn-primary'} flex-grow-1`}
              disabled={(!isBulkEdit && selectedWrestlers.length === 0) || isBonusBulkEditDisabled}
            >
              <i className={`bi ${isBulkEdit || editingParticipationId ? 'bi-arrow-repeat' : isLiveAnnouncementMode ? 'bi-broadcast' : participationMode === 'draft' ? 'bi-pencil-square' : 'bi-save'} me-1`}></i>
              {isBulkEdit ? `${primaryActionLabel} (${selectedParticipationsForEdit.length})` : primaryActionLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ParticipationForm;
